import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, X, ArrowRight, Droplets, Zap, Wrench, ShieldCheck, Download } from "lucide-react";
import { useLang } from "@/lang";
import { MaskLines, Reveal, Crosshair, EASE } from "@/components/Reveal";
import { PHONE, PHONE_TEL, EMAIL } from "@/content";
import { GROUPS, CATS, ITEMS, groupForCat } from "@/catalogData";

const HERO_BG =
    "https://images.pexels.com/photos/7937300/pexels-photo-7937300.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

function imgFor(item) {
    return item.img ? `/products/${item.code.replace(/\//g, ".")}.png` : "/products/_placeholder.png";
}

function SpecDrawer({ item, onClose }) {
    const { c } = useLang();
    const closeRef = useRef(null);
    const drawerRef = useRef(null);
    useEffect(() => {
        const trigger = document.activeElement;
        const onKey = (e) => {
            if (e.key === "Escape") {
                onClose();
                return;
            }
            if (e.key !== "Tab" || !drawerRef.current) return;
            const focusables = Array.from(drawerRef.current.querySelectorAll("button, a[href]"));
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener("keydown", onKey);
        closeRef.current?.focus();
        return () => {
            window.removeEventListener("keydown", onKey);
            if (trigger && trigger.focus) trigger.focus();
        };
    }, [onClose]);
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-ink/50 backdrop-blur-sm"
            onClick={onClose}
            data-testid="spec-drawer-backdrop"
        >
            <motion.aside
                ref={drawerRef}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.45, ease: EASE }}
                className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-pure shadow-lift"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`${lang_name(item)} — ${item.code}`}
                data-testid="spec-drawer"
            >
                <div className="flex items-center justify-between border-b border-line px-6 py-5">
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-sky-text">
                        {item.code}
                    </span>
                    <button
                        onClick={onClose}
                        ref={closeRef}
                        data-testid="spec-drawer-close"
                        aria-label="Close spec drawer"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink transition-colors hover:bg-ink hover:text-paper"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="rounded-2xl bg-paper p-8">
                        <img
                            src={imgFor(item)}
                            alt={lang_name(item)}
                            onError={(e) => { if (!e.currentTarget.src.endsWith("_placeholder.png")) e.currentTarget.src = "/products/_placeholder.png"; }}
                            className="mx-auto max-h-64 w-auto object-contain"
                        />
                    </div>
                    <h3 className="mt-6 text-2xl font-normal tracking-tight text-ink">
                        {lang_name(item)}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-soft">{lang_desc(item)}</p>
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-sky-text">
                        STAR USA / {item.cat} — {item.code}
                    </p>
                    <div className="mt-3 rounded-2xl border border-line">
                        {item.specs.filter((sp) => sp[1] && sp[1] !== "-").map((s, i, arr) => (
                            <div
                                key={s[0]}
                                className={`flex items-center justify-between px-4 py-3.5 ${i !== arr.length - 1 ? "border-b border-line" : ""}`}
                                data-testid="spec-drawer-row"
                            >
                                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-mist">
                                    {s[0]}
                                </span>
                                <span className="text-base font-medium text-ink">{s[1]}</span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 font-mono text-[11px] leading-relaxed text-mist">
                        {c.products.drawerNote}
                    </p>
                </div>
                <div className="border-t border-line p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                        {item.series}
                    </p>
                    <a
                        href={`tel:${PHONE_TEL}`}
                        data-testid="spec-drawer-call"
                        className="mt-3 flex items-center justify-center gap-3 rounded-full bg-ink py-4 text-base font-semibold text-paper transition-colors hover:bg-sky-deep"
                    >
                        <Phone className="h-4 w-4" strokeWidth={2.5} />
                        {PHONE}
                    </a>
                </div>
            </motion.aside>
        </motion.div>
    );
}

function lang_name(item) {
    return item._es ? item.nameEs : item.name;
}
function lang_desc(item) {
    return item._es ? item.descEs : item.desc;
}

export default function Products() {
    const { c, lang } = useLang();
    const [searchParams, setSearchParams] = useSearchParams();
    const catParam = searchParams.get("cat");
    const groupParam = searchParams.get("group");
    const [group, setGroup] = useState(() => {
        if (catParam) return groupForCat(catParam);
        return GROUPS.some((g) => g.key === groupParam) ? groupParam : "PLUMBING";
    });
    const [filter, setFilter] = useState(
        catParam && CATS.some((k) => k.key === catParam) ? catParam : "ALL"
    );
    const [specIdx, setSpecIdx] = useState(-1);
    const prevGroupRef = useRef(group);

    useEffect(() => {
        const nextGroup = catParam
            ? groupForCat(catParam)
            : GROUPS.some((g) => g.key === groupParam)
                ? groupParam
                : "PLUMBING";
        const groupChanged = nextGroup !== prevGroupRef.current;
        prevGroupRef.current = nextGroup;
        setGroup(nextGroup);
        setFilter(catParam && CATS.some((k) => k.key === catParam) ? catParam : "ALL");
        setSpecIdx(-1);
        if (groupChanged) {
            if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
            window.scrollTo(0, 0);
        }
    }, [catParam, groupParam]);

    const catsInGroup = CATS.filter((k) => k.group === group);
    const groupItems = ITEMS.filter((it) => groupForCat(it.cat) === group);
    const visible = filter !== "ALL" ? groupItems.filter((it) => it.cat === filter) : groupItems;
    const catCounts = catsInGroup.reduce(
        (m, k) => ({ ...m, [k.key]: groupItems.filter((it) => it.cat === k.key).length }),
        {}
    );
    const drawerItem = specIdx >= 0 ? { ...visible[specIdx], _es: lang === "es" } : null;

    const selectGroup = (key) => {
        setGroup(key);
        setFilter("ALL");
        setSpecIdx(-1);
        setSearchParams({ group: key });
    };
    const selectFilter = (key) => {
        setFilter(key);
        setSpecIdx(-1);
        const params = { group };
        if (key !== "ALL") params.cat = key;
        setSearchParams(params);
    };
    const groupLabel = (key) => {
        const g = GROUPS.find((x) => x.key === key);
        return lang === "es" ? g.es : g.en;
    };

    return (
        <>
            <section className="relative flex min-h-[70svh] items-end overflow-hidden" data-testid="products-hero">
                <div className="absolute inset-0 -z-10">
                    <img src={HERO_BG} alt="" className="h-full w-full scale-110 object-cover" />
                    <div className="absolute inset-0 bg-ink/60" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-transparent to-ink" />
                </div>
                <div className="container-x pb-16 pt-40">
                    <Reveal>
                        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky">
                            <Crosshair className="text-sky" />
                            {c.products.eyebrow}
                        </div>
                    </Reveal>
                    <h1 className="mt-6 text-[clamp(2.8rem,8vw,7rem)] font-light leading-[1.03] tracking-[-0.02em] text-paper">
                        <MaskLines mount lines={c.products.lines} step={0.13} delay={0.2} />
                    </h1>
                    <Reveal delay={0.4}>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-paper/75 sm:text-lg" dangerouslySetInnerHTML={{ __html: c.products.intro }} />
                    </Reveal>
                    <Reveal delay={0.5}>
                        <a
                            href="/star-usa-catalogue.pdf"
                            download="Star-USA-Catalogue.pdf"
                            data-testid="catalogue-download-button"
                            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-paper px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-white"
                        >
                            <Download className="h-4 w-4" strokeWidth={2.5} />
                            {c.products.downloadCta}
                            <span className="font-mono text-[11px] font-normal text-ink/50">{c.products.downloadMeta}</span>
                        </a>
                    </Reveal>
                </div>
            </section>

            <section className="container-x py-20 md:py-28" data-testid="products-grid-section">
                <h2 className="sr-only">Product catalogue</h2>
                <Reveal>
                    <div
                        className="mb-8 grid gap-3 sm:grid-cols-3"
                        data-testid="product-group-tabs"
                        role="group"
                        aria-label={lang === "es" ? "Grupos de productos" : "Product groups"}
                    >
                        {GROUPS.map((g) => {
                            const Icon = g.key === "PUMPS" ? Droplets : g.key === "ELECTRICAL" ? Zap : Wrench;
                            const count = ITEMS.filter((it) => groupForCat(it.cat) === g.key).length;
                            return (
                                <button
                                    key={g.key}
                                    onClick={() => selectGroup(g.key)}
                                    aria-pressed={group === g.key}
                                    data-testid={`product-group-tab-${g.key}`}
                                    className={`flex items-center justify-between rounded-2xl border px-6 py-5 text-left transition-all duration-200 ${
                                        group === g.key
                                            ? "border-ink bg-ink text-paper"
                                            : "border-line bg-pure text-ink hover:border-ink"
                                    }`}
                                >
                                    <span className="flex items-center gap-3 text-lg font-semibold tracking-tight">
                                        <Icon className="h-5 w-5 text-sky-text" />
                                        {lang === "es" ? g.es : g.en}
                                    </span>
                                    <span
                                        className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
                                            group === g.key ? "text-paper/60" : "text-mist"
                                        }`}
                                    >
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </Reveal>
                <Reveal delay={0.05}>
                    <div
                        className="mb-8 flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-paper px-5 py-4"
                        data-testid="products-certs-callout"
                    >
                        <ShieldCheck className="h-4 w-4 shrink-0 text-sky-text" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft">
                            {c.certs.label} — {c.certs.badges.map((b) => b.mark).join(" · ")}
                        </span>
                    </div>
                </Reveal>
                {catsInGroup.length > 0 && (
                    <Reveal>
                        <div className="mb-12 flex flex-wrap items-center gap-2" data-testid="product-filters">
                            <span className="mr-3 font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
                                {c.products.filterLabel}
                            </span>
                            {[{ key: "ALL", label: c.products.filters[0] }]
                                .concat(catsInGroup.map((k) => ({ key: k.key, label: lang === "es" ? k.es : k.en })))
                                .map((f) => (
                                    <button
                                        key={f.key}
                                        onClick={() => selectFilter(f.key)}
                                        aria-pressed={filter === f.key}
                                        data-testid={`product-filter-${f.key}`}
                                        className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                                            filter === f.key
                                                ? "border-ink bg-ink text-paper"
                                                : "border-line text-ink-soft hover:border-ink hover:text-ink"
                                        }`}
                                    >
                                        {f.label}
                                        <span className="ml-2 font-mono text-[11px] font-normal opacity-60">
                                            {f.key === "ALL" ? groupItems.length : catCounts[f.key] || 0}
                                        </span>
                                    </button>
                                ))}
                        </div>
                    </Reveal>
                )}

                {groupItems.length === 0 ? (
                    <div
                        className="flex flex-col items-center gap-6 rounded-3xl border border-line bg-paper p-12 text-center md:p-20"
                        data-testid="group-empty-panel"
                    >
                        <span className="flex h-16 w-16 items-center justify-center rounded-full border border-line bg-pure">
                            {group === "PUMPS" ? (
                                <Droplets className="h-7 w-7 text-sky-text" />
                            ) : (
                                <Zap className="h-7 w-7 text-sky-text" />
                            )}
                        </span>
                        <h3 className="text-3xl font-light tracking-tight text-ink sm:text-4xl">
                            {groupLabel(group)}
                        </h3>
                        <p className="max-w-xl text-base leading-relaxed text-ink-soft">
                            {c.groups.emptyLine.replace("{name}", groupLabel(group))}
                        </p>
                        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                            <a
                                href={`tel:${PHONE_TEL}`}
                                data-testid="group-empty-call"
                                className="flex items-center justify-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-paper transition-colors hover:bg-sky-deep"
                            >
                                <Phone className="h-4 w-4" strokeWidth={2.5} />
                                {c.hero.call}
                            </a>
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="group-empty-email"
                                className="flex items-center justify-center gap-2 rounded-full border border-line px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:border-ink"
                            >
                                <Mail className="h-4 w-4" />
                                {c.hero.email}
                            </a>
                        </div>
                    </div>
                ) : (
                <motion.div
                    key={group + "-" + filter + "-" + visible.length}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                >
                        {visible.map((item, i) => (
                            <Reveal key={item.code} delay={(i % 3) * 0.05}>
                                <article
                                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-pure transition-all duration-300 hover:-translate-y-1 hover:shadow-soft"
                                    data-testid={`product-card-${item.code}`}
                                >
                                    <div className="bg-paper p-8">
                                        <img
                                            src={imgFor(item)}
                                            alt={lang === "es" ? item.nameEs : item.name}
                                            loading="lazy"
                                            onError={(e) => { if (!e.currentTarget.src.endsWith("_placeholder.png")) e.currentTarget.src = "/products/_placeholder.png"; }}
                                            className="mx-auto h-52 w-full object-contain transition-transform duration-700 group-hover:scale-[1.06]"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col border-t border-line p-7">
                                        <div className="flex items-center justify-between">
                                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sky-text">
                                                {item.code}
                                            </p>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist">
                                                {item.series}
                                            </p>
                                        </div>
                                        <h3 className="mt-3 text-xl font-normal leading-snug tracking-tight text-ink">
                                            {lang === "es" ? item.nameEs : item.name}
                                        </h3>
                                        <p className="mt-2.5 flex-1 text-sm leading-relaxed text-ink-soft">
                                            {lang === "es" ? item.descEs : item.desc}
                                        </p>
                                        <div className="mt-6 flex items-center gap-4">
                                            <a
                                                href={`tel:${PHONE_TEL}`}
                                                data-testid={`product-call-button-${item.code}`}
                                                className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition-colors hover:bg-sky-deep"
                                            >
                                                <Phone className="h-3.5 w-3.5" strokeWidth={2.5} />
                                                {c.products.callToOrder}
                                            </a>
                                            <button
                                                onClick={() => setSpecIdx(i)}
                                                data-testid={`product-spec-button-${item.code}`}
                                                className="group/spec flex items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-sky-text"
                                            >
                                                {c.products.fullSpec}
                                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/spec:translate-x-1" />
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                        </motion.div>
                )}
            </section>

            <section className="bg-ink py-24 md:py-32" data-testid="family-wall-section">
                <div className="container-x">
                    <div className="mb-12 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky">
                        <span className="h-px w-10 bg-sky/60" />
                        {c.products.familiesLabel}
                    </div>
                    <Reveal>
                        <h2 className="max-w-2xl text-3xl font-light tracking-tight text-paper sm:text-5xl">
                            {c.products.familiesTitle}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-paper/10 bg-paper/10 md:grid-cols-3 lg:grid-cols-6">
                        {c.products.families.map((brand, i) => (
                            <Reveal key={brand} delay={i * 0.05}>
                                <div
                                    className="flex h-24 items-center justify-center bg-ink px-4 text-center transition-colors duration-300 hover:bg-ink-soft"
                                    data-testid={`family-cell-${i + 1}`}
                                >
                                    <span className="font-display text-lg font-extrabold tracking-tight text-paper/60 transition-colors duration-300 hover:text-sky sm:text-xl">
                                        {brand}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mt-8 font-mono text-xs uppercase tracking-[0.15em] text-paper/50">
                            {c.products.brandsNote}
                        </p>
                    </Reveal>
                </div>
            </section>

            <AnimatePresence>
                {drawerItem && <SpecDrawer item={drawerItem} onClose={() => setSpecIdx(-1)} />}
            </AnimatePresence>
        </>
    );
}
